import { IonContent, IonPage, useIonRouter } from "@ionic/react";
import { Redirect, RouteComponentProps } from "react-router-dom";

import { queryClient, useCollection, useResource } from "@/hooks/resources";
import { StageDisplay, StageInput } from "@/components/gameplay/stage";
import { Controls } from "@/components/gameplay/controls";
import { ZoomControls } from "@/components/gameplay/zoom-controls";
import { GameProvider } from "@/contexts/game-context";
import Song from "@/types/song-type";
import { useAppContext } from "@/contexts/app-context";
import SongPreference from "@/types/song-preference";
import AssessmentQuestionAnswer from "@/types/assessment-question-answer";
import { useEffect, useMemo } from "react";

const Gameplay = ({ match }: RouteComponentProps<{ id: string }>) => {
  const songId = match?.params?.id;
  const router = useIonRouter();

  const {
    state: {
      currentUser,
      isLoadingCurrentUser,
      hasActiveSubscription,
      selectedChildId,
    },
  } = useAppContext();

  const [song] = useResource<Song>(`/songs/${songId}`);
  const [songPreference, { isLoading: isLoadingSongPreference }] =
    useResource<SongPreference>(
      `/songs/${songId}/song_preference?child_id=${selectedChildId}`,
      { enabled: !!selectedChildId },
    );
  const [
    assessmentQuestionAnswers,
    { isLoading: isLoadingAssessmentQuestionAnswers },
  ] = useCollection<AssessmentQuestionAnswer>(
    `/assessment_question_answers?song_id=${songId}&child_id=${selectedChildId}&phase=pre_assessment`,
    { enabled: !!selectedChildId },
  );

  useEffect(() => {
    if (!song) return;

    queryClient.invalidateQueries({
      queryKey: [`/songs/progress?child_id=${selectedChildId}`],
    });

    if (selectedChildId) {
      queryClient.invalidateQueries({
        queryKey: [`/songs/assessments?child_id=${selectedChildId}`],
      });
    }
  }, [song, selectedChildId]);

  const maximumPreAssessmentQuestionAnswerNumber = useMemo(() => {
    if (!assessmentQuestionAnswers || !assessmentQuestionAnswers.length)
      return 0;
    return Math.max(
      ...assessmentQuestionAnswers.map(
        (answer) => answer.assessmentQuestion.number,
      ),
    );
  }, [assessmentQuestionAnswers]);

  const preAssessmentComplete = useMemo(() => {
    return (
      maximumPreAssessmentQuestionAnswerNumber >=
      Math.max(
        ...(song?.assessmentQuestions.map((question) => question.number) || []),
      )
    );
  }, [maximumPreAssessmentQuestionAnswerNumber, song?.assessmentQuestions]);

  const handleExit = () => {
    return router.canGoBack() ? router.goBack() : router.push("/songs");
  };

  if (!isLoadingCurrentUser && !hasActiveSubscription) {
    return <Redirect to="/songs" />;
  }

  return (
    <IonPage>
      <IonContent
        fullscreen
        className="bg-white"
        scrollY={false}
        scrollX={false}
      >
        {song &&
          !isLoadingSongPreference &&
          !isLoadingAssessmentQuestionAnswers && (
            <GameProvider
              song={song}
              assessmentEnabled={
                songPreference && songPreference.assessmentEnabled
              }
              initialPhase={
                currentUser?.entitlements.includes("assessments") &&
                songPreference &&
                songPreference.assessmentEnabled &&
                !preAssessmentComplete
                  ? "pre_assessment_prompt"
                  : "prompt"
              }
              assessmentQuestionNumber={
                maximumPreAssessmentQuestionAnswerNumber + 1
              }
            >
              <ZoomControls />
              <div className="flex flex-col h-full">
                <StageDisplay />

                <div className="flex flex-col md:flex-row border-t-2 border-gray-400">
                  <StageInput />

                  <div className="flex flex-col gap-x-2">
                    <div className="flex gap-4 items-center justify-between p-2">
                      <h3>Song: {song?.name}</h3>

                      <button
                        className="btn-default bg-red-500 hover:bg-red-600 text-white font-bold w-auto rounded-full py-2"
                        onClick={handleExit}
                      >
                        Exit
                      </button>
                    </div>

                    <Controls className="bg-gray-200 flex justify-between grow items-center p-2 gap-2" />
                  </div>
                </div>
              </div>
            </GameProvider>
          )}
      </IonContent>
    </IonPage>
  );
};

export default Gameplay;
